import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useOutletContext } from 'react-router-dom';
import { Home, Sparkles, Check, Globe } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { PLAN_MODULE_KEYS } from '../planModules';

// 🔑 Stripe publishable key – must be from the SAME account as backend STRIPE_SECRET_KEY (see .env REACT_APP_STRIPE_PUBLISHABLE_KEY)
let stripePromise = null;
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const getStripePromise = () => {
  if (!STRIPE_PUBLIC_KEY) return Promise.resolve(null);
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLIC_KEY)
      .catch((error) => {
        console.warn('⚠️ Erreur lors du chargement de Stripe:', error);
        // Retourner null pour indiquer que Stripe n'est pas disponible
        return null;
      });
  }
  return stripePromise;
};

// Taux de conversion depuis FCFA (base)
// Les prix sont stockés en FCFA dans la base de données
const EXCHANGE_RATES = {
  FCFA: 1,
  EUR: 1 / 655,      // 1 EUR = 655 FCFA
  USD: 1 / 600,      // 1 USD ≈ 600 FCFA (approximatif)
  GBP: 1 / 760,      // 1 GBP ≈ 760 FCFA (approximatif)
  NGN: 1 / 0.45,     // 1 NGN ≈ 0.45 FCFA (1 FCFA ≈ 2.22 NGN)
  JPY: 1 / 4,        // 1 JPY ≈ 4 FCFA (approximatif)
};

// Convertir FCFA vers une autre devise
const convertFCFAToCurrency = (fcfa, targetCurrency) => {
  if (!fcfa || typeof fcfa !== 'number') return 0;
  const rate = EXCHANGE_RATES[targetCurrency] || 1;
  return fcfa * rate;
};

// Formater le prix selon la devise
const formatPrice = (fcfa, currency) => {
  if (!fcfa || typeof fcfa !== 'number') return '0.00';
  
  const convertedAmount = convertFCFAToCurrency(fcfa, currency);
  
  if (currency === 'FCFA') {
    return convertedAmount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } else if (currency === 'JPY' || currency === 'NGN') {
    return convertedAmount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } else {
    return convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};

// Obtenir le symbole de la devise
const getCurrencySymbol = (currency) => {
  const symbols = {
    FCFA: 'FCFA',
    EUR: 'EUR',
    USD: 'USD',
    GBP: 'GBP',
    NGN: 'NGN',
    JPY: 'JPY'
  };
  return symbols[currency] || 'FCFA';
};

const BillingPlans = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { onRefetchBilling } = useOutletContext() || {};
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem('billing_currency') || 'FCFA';
  });

  // Fresh billing status when opening plans (e.g. from "Free trial ended" or after payment)
  useEffect(() => {
    if (onRefetchBilling) onRefetchBilling();
  }, []);

  // Feature list per plan: use shared module config (same as landing page) for automatic setup
  const getPlanFeatureLabels = (plan) => {
    const keys = plan.id && PLAN_MODULE_KEYS[plan.id];
    if (keys && keys.length) {
      return keys.map((key) => t(`landing.${key}`));
    }
    return (plan.features || []).map((f) => getFeatureDisplay(f));
  };

  // Charger les plans depuis l'API
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Utiliser API_BASE depuis config.js
      const response = await fetch(`${API_BASE}/api/billing/plans`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Pas d'en-tête Authorization ici - c'est pour le frontend
        },
        credentials: 'include'  // Inclure les cookies si nécessaire
      });
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📋 Plans reçus:', data);
      
      if (data.plans && Array.isArray(data.plans)) {
        // Vérifier et logger les prix convertis
        data.plans.forEach(plan => {
          console.log(`💰 Plan ${plan.name}: ${plan.monthlyPrice} ${plan.currency || 'FCFA'}/mois + ${plan.pricePerEmployee} ${plan.currency || 'FCFA'}/employé`);
        });
        setPlans(data.plans);
      } else {
        throw new Error('Format de données invalide');
      }
      
    } catch (err) {
      console.error('❌ Erreur chargement plans:', err);
      setError(`Impossible de charger les plans: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Démarrer le processus de paiement
  const handleSelectPlan = async (planId) => {
    setProcessingPayment(planId);
    setError('');

    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Vous devez être connecté pour sélectionner un plan');
      }

      // Utiliser API_BASE depuis config.js
      console.log('🚀 Création session paiement pour plan:', planId);
      
      // Créer la session de paiement
      // Le backend calculera automatiquement le nombre d'employés actifs
      const response = await fetch(`${API_BASE}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ 
          planId
          // employeeCount sera calculé automatiquement par le backend depuis la base de données
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erreur ${response.status}`);
      }

      const { sessionId } = await response.json();
      console.log('✅ Session ID reçu:', sessionId);
      
      if (!sessionId) {
        throw new Error('Session ID manquant dans la réponse');
      }
      
      // Rediriger vers Stripe Checkout
      try {
        const stripe = await getStripePromise();
        if (!stripe) {
          throw new Error('Stripe non initialisé. Vérifiez votre connexion internet.');
        }
        
        const { error: stripeError } = await stripe.redirectToCheckout({ sessionId });
        
        if (stripeError) {
          throw new Error(stripeError.message);
        }
      } catch (stripeError) {
        // Si Stripe n'a pas pu être chargé, afficher un message d'erreur clair
        const errorMessage = stripeError?.message || stripeError?.toString() || 'Erreur inconnue';
        if (errorMessage.includes('Failed to load') || errorMessage.includes('load') || errorMessage.includes('network')) {
          throw new Error('Impossible de charger Stripe.js. Vérifiez votre connexion internet ou contactez le support.');
        }
        throw stripeError;
      }
      
    } catch (err) {
      console.error('❌ Erreur paiement:', err);
      setError(`Erreur de paiement: ${err.message}`);
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('billing_currency', currency);
  };

  // Fonction pour obtenir les fonctionnalités affichables
  const getFeatureDisplay = (featureKey) => {
    const featureMap = {
      'pointage': 'Attendance',
      'historique': 'History',
      'calendrier': 'Calendar',
      'planning': 'Planning',
      'communiques': 'Announcements',
      'dashboard': 'Dashboard',
      'employees': 'Employees',
      'reports': 'Reports',
      'badges': 'Digital Badges',
      'formations': 'Training Courses',
      'notes_frais': 'Expense Reports',
      'conges': 'Holidays',
      'soldes': 'Balance Management',
      'documents': 'Documents',
      'profil': 'Profile',
      'audit_photos': 'Audit Photos',
      'ideas': 'Ideas Box',
      'performance': 'Performance'
    };
    return featureMap[featureKey] || featureKey;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            borderTop: '4px solid #3b82f6',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Chargement des plans...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('billing.back_home_title')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: 700,
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Sparkles size={32} />
            {t('billing.plans_page_title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('billing.plans_page_subtitle')}
        </p>
        {/* Sélecteur de devise */}
        <div style={{
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Globe size={18} style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
          <div style={{ flex: 1, minWidth: 0, maxWidth: 220 }}>
            <CustomSelect
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              options={[
                { value: 'FCFA', label: t('billing.currency_fcfa') },
                { value: 'EUR', label: t('billing.currency_eur') },
                { value: 'USD', label: t('billing.currency_usd') },
                { value: 'GBP', label: t('billing.currency_gbp') },
                { value: 'NGN', label: t('billing.currency_ngn') },
                { value: 'JPY', label: t('billing.currency_jpy') }
              ]}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '8px 12px',
                fontWeight: 600
              }}
            />
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="plans-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '2px solid rgba(239, 68, 68, 0.4)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            color: '#fca5a5',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontWeight: 600 }}>⚠️ {error}</span>
            <button
              onClick={fetchPlans}
              style={{
                background: 'rgba(239, 68, 68, 0.3)',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
              }}
            >
              🔄 {t('billing.retry')}
            </button>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          marginBottom: '40px'
        }}>
        {plans.map((plan, index) => (
          <div
            key={plan.id}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: plan.id === 'business'
                ? '2px solid rgba(59, 130, 246, 0.5)'
                : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '28px',
              position: 'relative',
              boxShadow: plan.id === 'business'
                ? '0 8px 32px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)'
                : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = plan.id === 'business'
                ? '0 12px 40px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
                : '0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = plan.id === 'business'
                ? '0 8px 32px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)'
                : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)';
            }}
          >
            {plan.id === 'business' && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                color: '#ffffff',
                padding: '6px 20px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
              }}>
                ⭐ {t('billing.recommended')}
              </div>
            )}
            
            <div style={{ marginBottom: '24px', paddingTop: plan.id === 'business' ? '12px' : '0' }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: 700,
                marginBottom: '16px'
              }}>
                {plan.id ? t(`landing.plan_${plan.id}`) : plan.name}
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginBottom: '8px'
              }}>
                {/* Prix principal: si base 0, afficher uniquement le prix par employé */}
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '4px'
                }}>
                  <span style={{ fontSize: '48px', fontWeight: 700, color: '#ffffff' }}>
                    {plan.monthlyPrice === 0
                      ? (typeof plan.pricePerEmployee === 'number' ? formatPrice(plan.pricePerEmployee, selectedCurrency) : plan.pricePerEmployee)
                      : (typeof plan.monthlyPrice === 'number' ? formatPrice(plan.monthlyPrice, selectedCurrency) : plan.monthlyPrice)}
                  </span>
                  <span style={{ fontSize: '20px', color: 'rgba(255, 255, 255, 0.8)', marginLeft: '4px' }}>
                    {getCurrencySymbol(selectedCurrency)}
                  </span>
                  <span style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.7)' }}>
                    {plan.monthlyPrice === 0
                      ? t('billing.per_employee_month')
                      : `${t('billing.per_month_only')} ${t('billing.per_employee_short')}`}
                  </span>
                </div>
              </div>
              {/* Form plan comment (+ X EUR/employee) removed from UI for all plans */}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Check size={18} />
                {t('billing.features_included')}
              </h4>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {getPlanFeatureLabels(plan).map((label, idx) => (
                  <li key={idx} style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Check size={16} style={{ color: '#34d399', flexShrink: 0 }} />
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div style={{
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <span style={{
                  display: 'block',
                  color: '#34d399',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '4px'
                }}>
                  🎁 {t('billing.free_trial_14')}
                </span>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '12px',
                  margin: 0
                }}>
                  {t('billing.no_commitment')}
                </p>
              </div>
              
              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={processingPayment === plan.id}
                style={{
                  width: '100%',
                  background: processingPayment === plan.id
                    ? 'rgba(107, 114, 128, 0.5)'
                    : plan.id === 'business'
                    ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '14px 24px',
                  borderRadius: '12px',
                  cursor: processingPayment === plan.id ? 'not-allowed' : 'pointer',
                  fontSize: '15px',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  boxShadow: processingPayment === plan.id
                    ? 'none'
                    : '0 4px 16px rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (processingPayment !== plan.id) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (processingPayment !== plan.id) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)';
                  }
                }}
              >
                {processingPayment === plan.id ? (
                  <>
                    <div style={{
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '50%',
                      borderTop: '2px solid #ffffff',
                      width: '16px',
                      height: '16px',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    {t('billing.processing')}
                  </>
                ) : (
                  `🚀 ${t('billing.start_now_btn')}`
                )}
              </button>
            </div>
          </div>
        ))}
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px',
          marginTop: '40px',
          textAlign: 'center'
        }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '24px',
          marginBottom: '24px',
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.8)'
        }}>
          <span>🔒 {t('billing.payment_secure')}</span>
          <span>💳 {t('billing.cards_accepted')}</span>
          <span>🛡️ {t('billing.data_protected')}</span>
        </div>
        
        <div style={{
          paddingTop: '24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>❓ {t('billing.questions_support')}</p>
          <p style={{ margin: 0 }}>📧 {t('billing.contact_info')}</p>
        </div>
        </div>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (min-width: 768px) {
          .plans-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .plans-content {
            padding: 180px 60px 60px !important;
          }
        }
        .plans-content {
          animation: slideUp 0.4s ease-out;
        }
        .billing-plans {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
          color: white;
        }

        .plans-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .plans-header h1 {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .error-banner {
          background: rgba(244, 67, 54, 0.9);
          padding: 1rem;
          border-radius: 8px;
          margin: 1rem 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .retry-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          color: white;
          cursor: pointer;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          margin-bottom: 3rem;
        }

        .plan-card {
          background: rgba(255, 255, 255, 0.95);
          color: #333;
          border-radius: 16px;
          padding: 2rem;
          position: relative;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .plan-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }

        .plan-card.recommended {
          border: 3px solid #4CAF50;
          transform: scale(1.05);
        }

        .recommended-badge {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: #4CAF50;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: bold;
          font-size: 0.9rem;
        }

        .plan-header {
          text-align: center;
          margin-bottom: 2rem;
          border-bottom: 2px solid #f0f0f0;
          padding-bottom: 1rem;
        }

        .plan-header h3 {
          font-size: 1.8rem;
          margin-bottom: 1rem;
          color: #2c3e50;
        }

        .plan-price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          margin-bottom: 0.5rem;
        }

        .currency {
          font-size: 1.2rem;
          color: #666;
        }

        .amount {
          font-size: 3rem;
          font-weight: bold;
          color: #2c3e50;
          margin: 0 0.2rem;
        }

        .period {
          font-size: 1rem;
          color: #666;
        }

        .price-detail {
          color: #666;
          font-size: 0.9rem;
        }

        .plan-features ul {
          list-style: none;
          padding: 0;
        }

        .plan-features li {
          padding: 0.5rem 0;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.95rem;
        }

        .plan-features li:last-child {
          border-bottom: none;
        }

        .plan-footer {
          margin-top: 2rem;
        }

        .trial-info {
          text-align: center;
          margin-bottom: 1rem;
        }

        .trial-badge {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          color: white;
          padding: 0.3rem 0.8rem;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: bold;
        }

        .trial-info p {
          font-size: 0.8rem;
          color: #666;
          margin: 0.5rem 0;
        }

        .select-plan-btn {
          width: 100%;
          padding: 1rem 2rem;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .select-plan-btn.primary {
          background: linear-gradient(45deg, #4CAF50, #45a049);
          color: white;
        }

        .select-plan-btn.secondary {
          background: linear-gradient(45deg, #2196F3, #1976D2);
          color: white;
        }

        .select-plan-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        }

        .select-plan-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .plans-footer {
          text-align: center;
          margin-top: 3rem;
        }

        .security-badges {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .security-badges span {
          background: rgba(255,255,255,0.2);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
        }

        .support-info p {
          margin: 0.5rem 0;
          opacity: 0.9;
        }

        .loading {
          text-align: center;
          padding: 4rem 2rem;
        }

        .spinner {
          border: 4px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top: 4px solid white;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .spinner-small {
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top: 2px solid white;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .plans-grid {
            grid-template-columns: 1fr;
          }
          
          .plan-card.recommended {
            transform: none;
          }
          
          .security-badges {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default BillingPlans;